import React, {
  useState,
  useCallback,
  useImperativeHandle,
  useRef,
  ForwardedRef,
  forwardRef,
  useMemo,
} from 'react';

import classNames from 'classnames';
import { useQuery, useQueryCache } from 'react-query';
import { Modal } from 'src/common/components';
import { Toast } from 'src/common/components';
import { Icon } from 'src/common/components/Icon';
import { ModalRefProps } from 'src/common/components/Modal';
import { Select } from 'src/common/components/Select';
import { OrbitIcons } from 'src/common/constants.';

import {
  createUser,
  createSharedAccountUsers,
  fetchAccountUsers,
  fetchCosmosAccountWithUsers,
  createCosmosUser,
  CosmosUser,
} from '../api';
import { USER_MODAL_INPUT_CLASS, getApiErrorMessage } from '../constants';

/* ───────────────────────── types ───────────────────────── */

interface AddUserModalProps {
  accountId: number;
  /** Cosmos account UUID - if provided, will fetch users from Cosmos */
  cosmosAccountId?: string | null;
  /** Whether this account uses shared FreeSWITCH (has freeswitch_group_id) */
  isSharedAccount?: boolean;
  onUserCreated: () => void;
}

type ModalMode = 'select' | 'create';

interface FormState {
  cosmos_user_id: string;
  phone: string;
  is_admin: boolean;
  in_call_center: boolean;
}

interface NewUserFormState {
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  confirm_password: string;
  is_account_manager: boolean;
}

const INITIAL_FORM_STATE: FormState = {
  cosmos_user_id: '',
  phone: '',
  is_admin: false,
  in_call_center: true,
};

const INITIAL_NEW_USER_STATE: NewUserFormState = {
  username: '',
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  confirm_password: '',
  is_account_manager: false,
};

/* ───────────────────────── helpers ───────────────────────── */

/** Validate cosmos_user_id is a positive integer */
const isValidCosmosUserId = (value: string): boolean => {
  const num = Number(value);
  return Number.isInteger(num) && num > 0;
};

/** Validate phone extension is a valid 4-digit number (1000-9999) for shared accounts */
const isValidSharedPhoneExtension = (value: string): boolean => {
  if (!value.trim()) return false;
  const num = Number(value);
  return Number.isInteger(num) && num >= 1000 && num <= 9999;
};

/** Validate email format */
const isValidEmail = (email: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

/** Validate username format (4-16 chars, alphanumeric + @.+-_) */
const isValidUsername = (username: string): boolean => {
  return /^[\w.@+-]{4,16}$/.test(username);
};

/** Format user option for display in select */
interface UserOption {
  id: string;
  label: string;
  user: CosmosUser;
}

const formatUserOption = (user: CosmosUser): UserOption => ({
  id: String(user.id),
  label: `${user.first_name} ${user.last_name}`.trim() || user.username,
  user,
});

/* ───────────────────────── main component ─────────────────── */

export const AddUserModal = forwardRef(
  (
    { accountId, cosmosAccountId, isSharedAccount = false, onUserCreated }: AddUserModalProps,
    ref: ForwardedRef<ModalRefProps>
  ) => {
    const [formData, setFormData] = useState<FormState>({ ...INITIAL_FORM_STATE });
    const [newUserData, setNewUserData] = useState<NewUserFormState>({ ...INITIAL_NEW_USER_STATE });
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [mode, setMode] = useState<ModalMode>('select');
    const [useManualInput, setUseManualInput] = useState(false);
    const modalRef = useRef<ModalRefProps>(null);
    const queryCache = useQueryCache();

    // Fetch cosmos users when modal is open and cosmosAccountId is available
    const { data: cosmosData, isLoading: cosmosLoading } = useQuery(
      ['cosmos-account-users', cosmosAccountId],
      () => (cosmosAccountId ? fetchCosmosAccountWithUsers(cosmosAccountId) : null),
      {
        enabled: isModalOpen && !!cosmosAccountId,
        staleTime: 30000,
      }
    );

    // Fetch existing interstellar users to filter them out
    const { data: interstellarUsers } = useQuery(
      ['interstellar-account-users', accountId],
      () => fetchAccountUsers(accountId),
      {
        enabled: isModalOpen,
        staleTime: 30000,
      }
    );

    // Filter out users already in interstellar
    const availableUsers = useMemo(() => {
      if (!cosmosData?.users) return [];
      const existingCosmosIds = new Set(
        (interstellarUsers?.users ?? [])
          .map((u) => (u.cosmos_user_id != null ? String(u.cosmos_user_id) : null))
          .filter((id): id is string => id != null)
      );
      return cosmosData.users
        .filter((u) => !existingCosmosIds.has(String(u.id)))
        .map(formatUserOption);
    }, [cosmosData?.users, interstellarUsers?.users]);

    const alreadyAddedCount = (cosmosData?.users?.length ?? 0) - availableUsers.length;

    useImperativeHandle(ref, () => ({
      show: () => {
        setIsModalOpen(true);
        setMode('select');
        setUseManualInput(false);
        setFormData({ ...INITIAL_FORM_STATE });
        setNewUserData({ ...INITIAL_NEW_USER_STATE });
        modalRef.current?.show();
      },
      hide: () => {
        setIsModalOpen(false);
        modalRef.current?.hide();
      },
    }));

    const handleFieldChange = useCallback((key: keyof FormState, value: string | boolean) => {
      setFormData((prev) => ({ ...prev, [key]: value }));
    }, []);

    const handleNewUserFieldChange = useCallback(
      (key: keyof NewUserFormState, value: string | boolean) => {
        setNewUserData((prev) => ({ ...prev, [key]: value }));
      },
      []
    );

    const handleUserSelect = useCallback((option: UserOption | null) => {
      if (option) {
        setFormData((prev) => ({
          ...prev,
          cosmos_user_id: option.id,
          is_admin: option.user.is_account_manager,
        }));
      } else {
        setFormData((prev) => ({ ...prev, cosmos_user_id: '' }));
      }
    }, []);

    // Validation for existing user selection
    const isCosmosIdValid = isValidCosmosUserId(formData.cosmos_user_id);
    const isPhoneValid = isSharedAccount ? isValidSharedPhoneExtension(formData.phone) : true;
    const isSelectFormValid = isCosmosIdValid && isPhoneValid;

    // Validation for new user creation
    const isNewUserFormValid =
      isValidUsername(newUserData.username) &&
      newUserData.first_name.trim().length > 0 &&
      newUserData.last_name.trim().length > 0 &&
      isValidEmail(newUserData.email) &&
      newUserData.password.length >= 6 &&
      newUserData.password === newUserData.confirm_password &&
      isPhoneValid;

    const isFormValid = mode === 'select' ? isSelectFormValid : isNewUserFormValid;

    const handleCreate = async (): Promise<void> => {
      if (!isFormValid) return;
      setLoading(true);

      try {
        let cosmosUserId: number;

        if (mode === 'create') {
          // First create user in Cosmos
          if (!cosmosAccountId) {
            Toast.error('No se puede crear usuario: falta el ID de cuenta de Cosmos');
            return;
          }

          const newUser = await createCosmosUser({
            account: cosmosAccountId,
            username: newUserData.username,
            first_name: newUserData.first_name,
            last_name: newUserData.last_name,
            email: newUserData.email,
            password: newUserData.password,
            is_account_manager: newUserData.is_account_manager,
          });

          cosmosUserId = Number(newUser.id);
          Toast.success(`Usuario ${newUser.username} creado en Cosmos`);

          // Invalidate cosmos users cache
          queryCache.invalidateQueries(['cosmos-account-users', cosmosAccountId]);
        } else {
          cosmosUserId = Number(formData.cosmos_user_id);
        }

        // Then add to Interstellar
        if (isSharedAccount) {
          await createSharedAccountUsers(accountId, {
            users: [
              {
                cosmos_user_id: cosmosUserId,
                phone: Number(formData.phone),
                is_admin: mode === 'create' ? newUserData.is_account_manager : formData.is_admin,
                in_call_center: formData.in_call_center,
              },
            ],
          });
        } else {
          const payload = {
            cosmos_user_id: cosmosUserId,
            is_admin: mode === 'create' ? newUserData.is_account_manager : formData.is_admin,
            ...(formData.phone.trim() !== '' ? { phone: Number(formData.phone) } : {}),
          };
          await createUser(accountId, [payload]);
        }

        Toast.success('Usuario agregado a Interstellar exitosamente');
        setFormData({ ...INITIAL_FORM_STATE });
        setNewUserData({ ...INITIAL_NEW_USER_STATE });
        onUserCreated();
        setIsModalOpen(false);
        modalRef.current?.hide();
      } catch (err: unknown) {
        Toast.error(
          getApiErrorMessage(
            err,
            'Error al crear el usuario. Verifica los datos e intenta nuevamente.'
          )
        );
      } finally {
        setLoading(false);
      }
    };

    // Determine if we should show the select or manual input
    const showUserSelect = cosmosAccountId && !useManualInput && mode === 'select';
    const selectedUser = availableUsers.find((u) => u.id === formData.cosmos_user_id);

    return (
      <Modal
        ref={modalRef}
        showFooter={false}
        actionFunc={() => void handleCreate()}
        actionText="Crear Usuario"
        cancelText="Cancelar"
      >
        <div className="max-h-[70vh] overflow-y-auto pr-2">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-2.0/10">
              <Icon name={OrbitIcons.UserPlus} size={20} className="text-primary-2.0" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-black-1.0">Nuevo Usuario</h3>
              <p className="text-xs text-gray-2.0">
                {mode === 'select'
                  ? 'Selecciona un usuario existente de Cosmos'
                  : 'Crea un nuevo usuario en Cosmos'}
              </p>
            </div>
          </div>

          {/* Mode toggle */}
          {cosmosAccountId && (
            <div className="flex gap-1 p-1 mb-4 rounded-lg bg-white-1.0">
              <button
                type="button"
                onClick={() => setMode('select')}
                className={classNames(
                  'flex-1 px-3 py-2 text-xs font-medium rounded-md transition-colors',
                  mode === 'select'
                    ? 'bg-white-0.0 text-primary-2.0 shadow-sm'
                    : 'text-gray-2.0 hover:text-black-1.0'
                )}
              >
                Seleccionar existente
              </button>
              <button
                type="button"
                onClick={() => setMode('create')}
                className={classNames(
                  'flex-1 px-3 py-2 text-xs font-medium rounded-md transition-colors',
                  mode === 'create'
                    ? 'bg-white-0.0 text-primary-2.0 shadow-sm'
                    : 'text-gray-2.0 hover:text-black-1.0'
                )}
              >
                Crear nuevo
              </button>
            </div>
          )}

          {/* Shared account indicator */}
          {isSharedAccount && (
            <div className="bg-green-50 flex items-center gap-2 px-3 py-2 mb-4 border border-green-200 rounded-lg">
              <Icon name={OrbitIcons.Cloud} size={16} className="text-green-600" />
              <span className="text-xs font-medium text-green-700">
                Cuenta con FreeSWITCH compartido
              </span>
            </div>
          )}

          <div className="gap-y-3 grid grid-cols-1">
            {mode === 'select' ? (
              /* ─────────── SELECT EXISTING USER ─────────── */
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-gray-3.0">
                    Usuario de Cosmos<span className="ml-0.5 text-red-500">*</span>
                  </label>
                  {cosmosAccountId && (
                    <button
                      type="button"
                      onClick={() => {
                        setUseManualInput(!useManualInput);
                        setFormData((prev) => ({ ...prev, cosmos_user_id: '' }));
                      }}
                      className="text-[10px] font-medium text-primary-2.0 hover:underline"
                    >
                      {useManualInput ? 'Seleccionar de lista' : 'Ingresar ID manual'}
                    </button>
                  )}
                </div>

                {showUserSelect ? (
                  <>
                    {cosmosLoading ? (
                      <div className="flex items-center gap-2 rounded-md border border-white-2.0 bg-white-0.0 px-3 py-2.5 text-sm text-gray-2.0">
                        <Icon name={OrbitIcons.RefreshCw} size={14} className="animate-spin" />
                        Cargando usuarios de Cosmos...
                      </div>
                    ) : availableUsers.length === 0 ? (
                      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-700">
                        {cosmosData?.users?.length === 0
                          ? 'No hay usuarios en esta cuenta de Cosmos'
                          : 'Todos los usuarios ya fueron agregados a Interstellar'}
                      </div>
                    ) : (
                      <Select
                        options={availableUsers}
                        fieldId="id"
                        fieldLabel="label"
                        value={formData.cosmos_user_id || null}
                        onChange={(opt: unknown) => handleUserSelect(opt as UserOption | null)}
                        placeholder="Buscar usuario por nombre..."
                        isClearable
                        menuPortalTarget={document.body}
                        formatOptionLabel={(option: unknown) => {
                          const opt = option as UserOption;
                          return (
                            <div className="flex flex-col py-0.5">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{opt.label}</span>
                                {!opt.user.is_active && (
                                  <span className="rounded bg-amber-100 px-1 py-0.5 text-[9px] font-medium text-amber-700">
                                    Inactivo
                                  </span>
                                )}
                                {opt.user.is_account_manager && (
                                  <span className="rounded bg-blue-100 px-1 py-0.5 text-[9px] font-medium text-blue-700">
                                    Admin
                                  </span>
                                )}
                              </div>
                              <span className="text-[11px] text-gray-2.0">
                                {opt.user.email} · ID: {opt.id}
                              </span>
                            </div>
                          );
                        }}
                      />
                    )}
                    {availableUsers.length > 0 && alreadyAddedCount > 0 && (
                      <p className="mt-1 text-[10px] text-gray-2.0">
                        {availableUsers.length} disponibles · {alreadyAddedCount} ya agregados
                      </p>
                    )}
                    {selectedUser && (
                      <div className="mt-2 rounded-md border border-white-2.0 bg-white-1.0 px-3 py-2">
                        <p className="text-xs text-gray-3.0">
                          <span className="font-medium">{selectedUser.user.username}</span> ·{' '}
                          {selectedUser.user.email}
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <input
                      id="add-user-cosmos-id"
                      type="number"
                      placeholder="Ej: 12345"
                      value={formData.cosmos_user_id}
                      onChange={(e) => handleFieldChange('cosmos_user_id', e.target.value)}
                      className={USER_MODAL_INPUT_CLASS}
                    />
                    {formData.cosmos_user_id && !isCosmosIdValid && (
                      <p className="mt-1 text-[10px] text-red-500">
                        Debe ser un número entero positivo
                      </p>
                    )}
                  </>
                )}
              </div>
            ) : (
              /* ─────────── CREATE NEW USER ─────────── */
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-3.0">
                      Nombre<span className="ml-0.5 text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Juan"
                      value={newUserData.first_name}
                      onChange={(e) => handleNewUserFieldChange('first_name', e.target.value)}
                      className={USER_MODAL_INPUT_CLASS}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-3.0">
                      Apellido<span className="ml-0.5 text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Pérez"
                      value={newUserData.last_name}
                      onChange={(e) => handleNewUserFieldChange('last_name', e.target.value)}
                      className={USER_MODAL_INPUT_CLASS}
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-3.0">
                    Username<span className="ml-0.5 text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="jperez"
                    value={newUserData.username}
                    onChange={(e) => handleNewUserFieldChange('username', e.target.value)}
                    className={classNames(
                      USER_MODAL_INPUT_CLASS,
                      newUserData.username &&
                        !isValidUsername(newUserData.username) &&
                        'border-red-500'
                    )}
                  />
                  {newUserData.username && !isValidUsername(newUserData.username) && (
                    <p className="mt-1 text-[10px] text-red-500">
                      4-16 caracteres, solo letras, números y @.+-_
                    </p>
                  )}
                </div>

                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-3.0">
                    Email<span className="ml-0.5 text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    placeholder="juan@ejemplo.com"
                    value={newUserData.email}
                    onChange={(e) => handleNewUserFieldChange('email', e.target.value)}
                    className={classNames(
                      USER_MODAL_INPUT_CLASS,
                      newUserData.email && !isValidEmail(newUserData.email) && 'border-red-500'
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-3.0">
                      Contraseña<span className="ml-0.5 text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      placeholder="••••••"
                      value={newUserData.password}
                      onChange={(e) => handleNewUserFieldChange('password', e.target.value)}
                      className={classNames(
                        USER_MODAL_INPUT_CLASS,
                        newUserData.password && newUserData.password.length < 6 && 'border-red-500'
                      )}
                    />
                    {newUserData.password && newUserData.password.length < 6 && (
                      <p className="mt-1 text-[10px] text-red-500">Mínimo 6 caracteres</p>
                    )}
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-3.0">
                      Confirmar<span className="ml-0.5 text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      placeholder="••••••"
                      value={newUserData.confirm_password}
                      onChange={(e) => handleNewUserFieldChange('confirm_password', e.target.value)}
                      className={classNames(
                        USER_MODAL_INPUT_CLASS,
                        newUserData.confirm_password &&
                          newUserData.password !== newUserData.confirm_password &&
                          'border-red-500'
                      )}
                    />
                    {newUserData.confirm_password &&
                      newUserData.password !== newUserData.confirm_password && (
                        <p className="mt-1 text-[10px] text-red-500">No coinciden</p>
                      )}
                  </div>
                </div>

                {/* Is Account Manager toggle */}
                <div className="flex items-center gap-3 rounded-md border border-white-2.0 bg-white-0.0 px-3 py-3">
                  <label htmlFor="new-user-is-manager" className="flex-1 cursor-pointer">
                    <p className="text-sm font-medium text-black-1.0">Administrador de cuenta</p>
                    <p className="text-xs text-gray-2.0">
                      Permisos de admin en Cosmos y en Interstellar
                    </p>
                  </label>
                  <button
                    id="new-user-is-manager"
                    type="button"
                    role="switch"
                    aria-checked={newUserData.is_account_manager}
                    onClick={() =>
                      handleNewUserFieldChange(
                        'is_account_manager',
                        !newUserData.is_account_manager
                      )
                    }
                    className={classNames(
                      'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-2.0 focus:ring-offset-2',
                      newUserData.is_account_manager ? 'bg-primary-2.0' : 'bg-gray-1.0'
                    )}
                  >
                    <span
                      className={classNames(
                        'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                        newUserData.is_account_manager ? 'translate-x-5' : 'translate-x-0'
                      )}
                    />
                  </button>
                </div>
              </>
            )}

            {/* Phone extension - required for shared, optional for dedicated */}
            <div>
              <label
                htmlFor="add-user-phone"
                className="mb-1 block text-xs font-medium text-gray-3.0"
              >
                Extension SIP {isSharedAccount ? '(1000-9999)' : '(0-9999)'}
                {isSharedAccount && <span className="ml-0.5 text-red-500">*</span>}
              </label>
              <input
                id="add-user-phone"
                type="number"
                min={isSharedAccount ? 1000 : 0}
                max={9999}
                placeholder={isSharedAccount ? 'Ej: 1001 (4 dígitos)' : 'Ej: 1001 (opcional)'}
                value={formData.phone}
                onChange={(e) => handleFieldChange('phone', e.target.value)}
                className={classNames(
                  USER_MODAL_INPUT_CLASS,
                  isSharedAccount && formData.phone && !isPhoneValid && 'border-red-500'
                )}
              />
              {isSharedAccount && (
                <p className="mt-1 text-[10px] text-gray-2.0">
                  Extensión de 4 dígitos requerida en cuentas compartidas
                </p>
              )}
              {isSharedAccount && formData.phone && !isPhoneValid && (
                <p className="mt-1 text-[10px] text-red-500">
                  Debe ser un número de 4 dígitos (1000-9999)
                </p>
              )}
            </div>

            {/* In Call Center toggle - only for shared accounts */}
            {isSharedAccount && (
              <div className="flex items-center gap-3 rounded-md border border-white-2.0 bg-white-0.0 px-3 py-3">
                <label htmlFor="add-user-in-call-center" className="flex-1 cursor-pointer">
                  <p className="text-sm font-medium text-black-1.0">Recibir llamadas</p>
                  <p className="text-xs text-gray-2.0">
                    Si se activa, el usuario puede recibir llamadas entrantes en las colas del call
                    center
                  </p>
                </label>
                <button
                  id="add-user-in-call-center"
                  type="button"
                  role="switch"
                  aria-checked={formData.in_call_center}
                  onClick={() => handleFieldChange('in_call_center', !formData.in_call_center)}
                  className={classNames(
                    'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-2.0 focus:ring-offset-2',
                    formData.in_call_center ? 'bg-primary-2.0' : 'bg-gray-1.0'
                  )}
                >
                  <span
                    className={classNames(
                      'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                      formData.in_call_center ? 'translate-x-5' : 'translate-x-0'
                    )}
                  />
                </button>
              </div>
            )}

            {/* Is Admin toggle - only for select mode */}
            {mode === 'select' && (
              <div className="flex items-center gap-3 rounded-md border border-white-2.0 bg-white-0.0 px-3 py-3">
                <label htmlFor="add-user-is-admin" className="flex-1 cursor-pointer">
                  <p className="text-sm font-medium text-black-1.0">Rol de Administrador</p>
                  <p className="text-xs text-gray-2.0">
                    Si se activa, el usuario tendra permisos de admin en la cuenta
                  </p>
                </label>
                <button
                  id="add-user-is-admin"
                  type="button"
                  role="switch"
                  aria-checked={formData.is_admin}
                  onClick={() => handleFieldChange('is_admin', !formData.is_admin)}
                  className={classNames(
                    'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-2.0 focus:ring-offset-2',
                    formData.is_admin ? 'bg-primary-2.0' : 'bg-gray-1.0'
                  )}
                >
                  <span
                    className={classNames(
                      'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                      formData.is_admin ? 'translate-x-5' : 'translate-x-0'
                    )}
                  />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={() => {
              setIsModalOpen(false);
              modalRef.current?.hide();
            }}
            className="btn btn-ghost flex items-center"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={!isFormValid || loading}
            onClick={() => void handleCreate()}
            className="btn btn-secondary flex items-center"
          >
            {loading ? (
              <>
                <span className="animate-spin border-t-transparent w-4 h-4 mr-2 border-2 border-white rounded-full" />
                {mode === 'create' ? 'Creando...' : 'Agregando...'}
              </>
            ) : (
              <>
                {mode === 'create' ? 'Crear y agregar' : 'Agregar usuario'}
                <Icon name={OrbitIcons.Check} size={14} className="ml-2" />
              </>
            )}
          </button>
        </div>
      </Modal>
    );
  }
);
